// §6.3 / §12 — the Windows 11 Start menu.
//
// A panel anchored above the taskbar, not a centred palette: search at the top, a pinned 6×3 grid,
// Recommended below it, and All apps a click away. Typing anywhere in it filters, because that is
// what Start does — the field owns the keyboard whether or not it has focus.
//
// Typing searches §12's index rather than filtering the app list — apps, files, open windows and
// settings, grouped by source — which is what the field has always claimed to do: "Search for apps
// and files". `Enter` "launches or focuses", and which of the two is the kernel's answer.
import { useEffect, useMemo, useRef, useState } from 'react';
import { USER_FOLDERS, joinVfs, userRoot, type FileEntry, type VfsPath } from '@hangar/shared';
import { ChevronLeft, ChevronRight, Search } from '@/assets/icons';
import { ProfileMenu } from '@/host/ProfileMenu';
import { COPY } from '@/copy';
import type { SearchEntry } from '@/kernel/search';
import { useKernel, useSearch, useStoreValue, useUnhealthyApps } from '@/kernel/react';

/** §6.3 — the pinned grid is six across and three down. */
export const PINNED_COLUMNS = 6;
export const PINNED_ROWS = 3;
export const PINNED_SLOTS = PINNED_COLUMNS * PINNED_ROWS;

/** How many files Recommended shows. Windows shows six; so does this. */
const RECOMMENDED_LIMIT = 6;

type Page = 'home' | 'all';

/** The user's most recently touched files, newest first (D-104). */
async function recentFiles(
  list: (dir: VfsPath) => Promise<FileEntry[]>,
  home: VfsPath,
): Promise<FileEntry[]> {
  const found: FileEntry[] = [];
  for (const folder of USER_FOLDERS) {
    try {
      const entries = await list(joinVfs(home, folder));
      for (const entry of entries) if (!entry.isDir) found.push(entry);
    } catch {
      // A folder that will not list is one folder, never an empty Start menu.
    }
  }
  return found.sort((a, b) => b.mtime - a.mtime).slice(0, RECOMMENDED_LIMIT);
}

export function Launcher() {
  const kernel = useKernel();
  const open = useStoreValue(kernel.shellui.store, (s) => s.overlay === 'launcher');
  const unhealthy = useUnhealthyApps();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState<Page>('home');
  const [selected, setSelected] = useState(0);
  const [recommended, setRecommended] = useState<FileEntry[] | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Recomputed each time the menu opens: an app installed since the last open must show up
  // without a reload (§9.6).
  const apps = useMemo(() => (open ? kernel.launch.launchable() : []), [kernel, open]);

  // §29.1 (D-369) — the All-apps page reads its headers from the kernel, so the three launchers
  // and the Store cannot disagree about which group a title is in.
  const sections = useMemo(() => (open ? kernel.launch.grouped() : []), [kernel, open]);

  const searching = query.trim().length > 0;
  const { groups, flat } = useSearch(query, open);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setPage('home');
    setSelected(0);
    inputRef.current?.focus();

    let live = true;
    void recentFiles((dir) => kernel.vfs.list(dir), userRoot(kernel.vfs.currentUser()))
      .then((files) => {
        if (live) setRecommended(files);
      })
      .catch(() => {
        if (live) setRecommended([]);
      });
    return () => {
      live = false;
    };
  }, [open, kernel]);

  useEffect(() => {
    setSelected((current) => Math.min(current, Math.max(0, flat.length - 1)));
  }, [flat.length]);

  if (!open) return null;

  const launch = (appId: string): void => {
    kernel.shellui.closeOverlay();
    kernel.launch.launch(appId);
  };

  // §12 — "`Enter` launches or focuses". One call, so a file opens in its default app and an open
  // window is brought forward rather than opened a second time.
  const activate = (entry: SearchEntry | undefined): void => {
    if (!entry) return;
    kernel.shellui.closeOverlay();
    kernel.activateSearchResult(entry);
  };

  // §9.5 — the click *is* the grant, which is why this goes through the kernel's shell-side
  // open rather than a bare launch: a window handed a file it may not read opens empty.
  const openFile = (entry: FileEntry): void => {
    kernel.shellui.closeOverlay();
    kernel.openPath(entry.path);
  };

  const pinned = apps.slice(0, PINNED_SLOTS);

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelected((i) => Math.min(i + 1, flat.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelected((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      activate(flat[selected]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      kernel.shellui.closeOverlay();
    }
  };

  const appTile = (app: { id: string; name: string }, testPrefix: string) => (
    <button
      key={app.id}
      type="button"
      className="win11-start-tile"
      data-testid={`${testPrefix}-${app.id}`}
      data-health={unhealthy.has(app.id) ? 'unhealthy' : 'ok'}
      onClick={() => launch(app.id)}
    >
      <span
        className="win11-start-tile-icon"
        aria-hidden
        dangerouslySetInnerHTML={{ __html: kernel.appIconSvg(app.id) }}
      />
      <span className="win11-start-tile-label">{app.name}</span>
    </button>
  );

  return (
    <div
      className="win11-start-backdrop"
      data-testid="launcher"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) kernel.shellui.closeOverlay();
      }}
    >
      <div className="win11-start-menu" role="dialog" aria-label={COPY.chrome.start}>
        <div className="win11-start-field">
          <Search size={16} aria-hidden />
          <input
            ref={inputRef}
            type="text"
            className="win11-start-input"
            data-testid="launcher-input"
            placeholder={COPY.chrome.startSearch}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage('home');
            }}
            onKeyDown={onKeyDown}
          />
        </div>

        {searching ? (
          <div className="win11-start-results" data-testid="launcher-results">
            {groups.map((group) => (
              <section
                key={group.source}
                className="win11-start-group"
                data-testid={`launcher-group-${group.source}`}
              >
                <h3 className="win11-start-group-name">{COPY.search[group.source]}</h3>
                <ul className="win11-start-list">
                  {group.results.map((result) => {
                    const entry = result.entry;
                    const index = flat.indexOf(entry);
                    return (
                      <li key={`${entry.source}:${entry.id}`}>
                        <button
                          type="button"
                          className="win11-start-result"
                          data-testid={`launcher-result-${entry.id}`}
                          data-source={entry.source}
                          data-health={
                            entry.source === 'app' && unhealthy.has(entry.id) ? 'unhealthy' : 'ok'
                          }
                          data-selected={String(index === selected)}
                          onPointerEnter={() => setSelected(index)}
                          onClick={() => activate(entry)}
                        >
                          <span className="win11-start-result-label">{entry.label}</span>
                          {entry.detail !== undefined && (
                            <span className="win11-start-result-detail">{entry.detail}</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
            {flat.length === 0 ? (
              <p className="win11-start-empty" data-testid="launcher-no-results">
                {COPY.chrome.startNoResults}
              </p>
            ) : null}
          </div>
        ) : page === 'all' ? (
          <section className="win11-start-section" data-testid="start-all-apps">
            <header className="win11-start-header">
              <button
                type="button"
                className="win11-start-more"
                data-testid="start-back"
                onClick={() => setPage('home')}
              >
                <ChevronLeft size={14} aria-hidden />
                {COPY.chrome.back}
              </button>
            </header>
            {/* §29.1 (D-369) — headed sections rather than one alphabetical run. Twenty Arcade
                titles in a flat list is a list; with headers it is a menu. */}
            {sections.map((group) => (
              <div key={`${group.category ?? ''}/${group.group ?? ''}`}>
                {group.category === null ? null : (
                  <h3
                    className="win11-start-group-name"
                    data-testid={`launcher-section-${group.group ?? group.category}`}
                  >
                    {group.group ?? group.category}
                  </h3>
                )}
                <ul className="win11-start-list" data-testid="start-all-apps-list">
                  {group.apps.map((app) => (
                    <li key={app.id}>
                      <button
                        type="button"
                        className="win11-start-result"
                        data-testid={`start-app-${app.id}`}
                        onClick={() => launch(app.id)}
                      >
                        {app.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        ) : (
          <>
            <section className="win11-start-section">
              <header className="win11-start-header">
                <h2 className="win11-start-title">{COPY.chrome.pinned}</h2>
                <button
                  type="button"
                  className="win11-start-more"
                  data-testid="start-all-apps-open"
                  onClick={() => setPage('all')}
                >
                  {COPY.chrome.allApps}
                  <ChevronRight size={14} aria-hidden />
                </button>
              </header>
              <div
                className="win11-start-grid"
                data-testid="start-pinned"
                data-columns={PINNED_COLUMNS}
              >
                {pinned.map((app) => appTile(app, 'start-pinned'))}
              </div>
            </section>

            <section className="win11-start-section">
              <header className="win11-start-header">
                <h2 className="win11-start-title">{COPY.chrome.recommended}</h2>
              </header>
              <div className="win11-start-recommended" data-testid="start-recommended">
                {recommended === null ? null : recommended.length === 0 ? (
                  <p className="win11-start-empty" data-testid="start-recommended-empty">
                    {COPY.chrome.recommendedEmpty}
                  </p>
                ) : (
                  recommended.map((entry) => (
                    <button
                      key={entry.path}
                      type="button"
                      className="win11-start-recent"
                      data-testid={`start-recent-${entry.name}`}
                      onClick={() => openFile(entry)}
                    >
                      <span className="win11-start-recent-name">{entry.name}</span>
                      <span className="win11-start-recent-where">{entry.path}</span>
                    </button>
                  ))
                )}
              </div>
            </section>
          </>
        )}

        {/* D-346 — the Start menu footer, which is where Windows has always put the account. */}
        <ProfileMenu prefix="windows11" />
      </div>
    </div>
  );
}
